import { createStore } from 'vuex';
import instance from '@/api/instance';
import * as lms from '@/api/lms';
import themes from '@/styles/themes.js';
import Task from '@/classes/Task';

// Tell the server which subjects to read. Without it the read fans out over
// every subject that exists (15 and growing) instead of the handful this user
// is in — each one a round trip against a remote database. `subjectsList` is
// populated by getSubjects at login; when it has not loaded yet the server
// falls back to reading all of them.
function subjectIdsParam(state) {
  const subjectIds = state.subjectsList?.map((e) => e._id).filter(Boolean) ?? [];
  return subjectIds.length ? { subjectIds: subjectIds.join(',') } : {};
}

export default createStore({
  state: (_) => ({
    isBrowserCompatible: true,
    isPageUnloadPrevented: false,
    loadingStep: 1,
    loadingProgress: 0,
    locale: undefined,
    theme: null,
    zoom: Number(localStorage.getItem('zoom') ?? 1),
    loader: {
      isVisible: false,
      bgColor: '#0003'
    },
    pagesTotal: 0,
    role: null,
    subjectId: null,
    users: [],
    categories: [],
    mediafiles: [],
    triggers: [],
    notifications: [],
    // Distinguishes "not fetched yet" from "fetched, genuinely empty", so the
    // drawer does not re-request every time it is opened.
    isNotificationsLoaded: false,
    isNotificationsLoading: false,
    // The badge. Fetched on page load independently of the list, which is only
    // loaded when the drawer is opened.
    notificationsUnread: 0,
    // Opaque paging position; null once the last page has been reached.
    notificationsCursor: null,
    notificationsHasMore: false,
    // The bell drawer's open state; see `navbar.vue`.
    isNotificationsDrawerOpen: false,
    templates: [],
    reports: [],
    jobs: [],
    tip: {},
    tips: [],
    glossary: [],
    certificate: {},
    surveyFields: [],
    subjectsList: [],
    subjects: [],
    subject: {},
    coursesList: [],
    courses: [],
    course: {},
    modules: [],
    lessonsList: [],
    lessons: [],
    lesson: {},
    tasks: [],
    task: { type: 'text' },
    groupsList: [],
    groups: [],
    group: {},
    students: [],
    student: {},
    persons: [],
    connections: [],
    connection: {},
    stats: {},
    userdata: {
      _id: 'Guest',
      avatar: false,
      email: undefined,
      username: 'Guest',
      settings: {}
    },
    settings: {
      telegram: undefined,
      instagram: undefined,
      dialoguesMode: 'messenger'
    },
    subjectOptions: {
      templates: {
        registration: undefined
      }
    },
    courseOptions: {
      isLinear: false,
      hasRating: false,
      hasDistribution: false,
      hasCertification: false,
      hasDeadline: false,
      hasSandbox: false,
      hasTips: false,
      certificateTemplate: undefined,
      certificationType: undefined,
      certificationValue: undefined,
      certificateBackground: false,
      hasCertificateSharing: false,
      certificateSharingText: undefined,
      certificateBackgroundV: 0,
      certificateDescription: undefined,
      certificateCourseType: undefined,
      certificateSkills: undefined,
      certificateAccreditationYear: undefined,
      examIds: [],
      topics: []
    },
    lessonOptions: {
      attemptsMax: 1,
      isLinear: false,
      isCumulative: false,
      isRatable: false,
      isUserSelectDisabled: false,
      isAssessment: false,
      connectionId: undefined
    }
  }),

  mutations: {
    preventUnload(state, payload) {
      state.isPageUnloadPrevented = payload;
    },
    setLocale(state, locale) {
      state.locale = locale;
    },
    setTheme(state, theme) {
      state.theme = theme;
      localStorage.setItem('theme', theme);
      Object.entries(themes[theme])?.forEach(([k, v]) => {
        document.documentElement.style.setProperty(k, v);
      });
    },
    setZoom(state, zoom) {
      zoom = Math.min(Math.max(+zoom, 0.5), 2);
      localStorage.setItem('zoom', zoom);
      state.zoom = zoom;
    },
    showLoader(state, payload) {
      if (typeof payload == 'boolean') state.loader.isVisible = payload;
      if (typeof payload == 'object') {
        state.loader.isVisible = true;
        state.loader.bgColor = payload.bgColor;
      }
    },
    updateState(state, payload) {
      for (const [k, v] of Object.entries(payload)) {
        if (Array.isArray(v) && v.every((item) => typeof item === 'object' && item !== null)) {
          state[k] = v.map((item, index) => ({ ...item, index }));
        } else {
          state[k] = v;
        }
      }
    },
    updateArrayOfObjects(state, { arrayName, fieldName, fieldValue, payload }) {
      const index = state[arrayName].findIndex((e) => e[fieldName] === fieldValue);
      Object.assign(state[arrayName][index], payload);
      //Object.assign(state[arrayName].find(e => e[fieldName] === fieldValue), payload)
    },
    updateArrayItem(state, { arrayName, value, index }) {
      if (index >= 0) state[arrayName][index] = value;
      else state[arrayName]?.push(value);
    },
    deleteArrayItem(state, { arrayName, index }) {
      state[arrayName]?.splice(index, 1);
    },
    setRole(state, payload) {
      state.role = payload;
    },
    setSubjectId(state, payload) {
      state.subjectId = payload;
    },
    updateUserdata(state, payload) {
      const { subjectsList, subject, user, adminDevSettings } = payload;
      if (subjectsList) state.subjectsList = subjectsList;
      if (subject) state.subject = subject;
      Object.assign(state.userdata, user);
      if (adminDevSettings) state.adminDevSettings = adminDevSettings;
      // Use key-presence check so null correctly clears the flag after stopping impersonation
      if ('impersonatingAdminEmail' in payload) {
        state.impersonatingAdminEmail = payload.impersonatingAdminEmail ?? null;
      }
    },
    setQuiz(state, { course, lesson, tasks, persons, reports, connections, glossary, tips, mediafiles }) {
      state.course = course;
      state.lesson = lesson;
      state.persons = persons;
      state.reports = reports;
      state.tips = tips;
      state.connections = connections;
      state.mediafiles = mediafiles;
      if (tasks) state.tasks = tasks.map((task) => new Task(task, glossary));
    },
    updateLesson(state, lessonData) {
      state.lesson = Object.assign(state.lesson, lessonData);
    },
    updateTask(state, taskData) {
      const taskId = taskData._id;
      const taskIndex = state.tasks?.findIndex((e) => e._id == taskId);
      state.tasks[taskIndex] = new Task({
        ...state.tasks[taskIndex],
        ...taskData
      });
    },
    setTask(state, taskData) {
      const { index } = taskData;
      state.tasks[index] = taskData;
    },
    pushTask(state, taskData) {
      const { index } = taskData;
      state.tasks.splice(index, 0, new Task(taskData));
    },
    deleteTask(state, { index }) {
      state.tasks.splice(index, 1);
    },
    rearrangeItems(state, { arrayName, index1, index2 }) {
      const temp1 = state[arrayName][index1];
      state[arrayName][index1] = state[arrayName][index2];
      state[arrayName][index2] = temp1;
    },
    setAuthorLessonSettings(state, { course, lesson, persons }) {
      if (!lesson.personIds) lesson.personIds = [];
      state.course = course;
      state.lesson = lesson;
      state.persons = persons;
    }
  },

  actions: {
    async getData({ commit }, path) {
      const { data } = await instance.get(path);
      commit('updateState', data);
    },
    async login({ dispatch }, { username, password }) {
      let {
        data: { accessToken }
      } = await instance.post(`/login`, { username, password });
      localStorage.setItem('accessToken', accessToken);
      dispatch('getUserdata');
    },
    async getAgenda({ commit, state }, { start, end }) {
      const { data } = await instance.get(`/${state.role}/agenda`, { params: { start, end } });
      commit('updateState', data);
    },
    async getUserdata({ commit, state }) {
      const { data } = await instance.get('/userdata');
      commit('updateUserdata', data);
      window.faro?.api?.setUser({
        _id: data.user._id,
        email: data.user.email,
        attributes: { role: state.role }
      });
    },
    async getSurveyFields({ commit }) {
      const { data } = await instance.get('/survey');
      commit('updateState', { surveyFields: data.surveyFields });
    },
    async getAdminDevSettings({ commit }) {
      const { data } = await instance.get('/admin-dev-settings');
      commit('updateUserdata', { adminDevSettings: data });
    },
    async getSubjects({ commit }) {
      const { data } = await instance.get('/subjects');
      commit('updateState', { subjectsList: data });
    },
    async getSubject({ commit, state }) {
      if (!state.subjectId) return;
      const { data } = await instance.get(`/${state.role}/subject`);
      commit('updateState', data);
    },
    // Subject-independent, unlike getSubject: the navbar bell also renders on
    // pages whose route has no :subjectId (the main page), where getSubject
    // early-returns and would leave notifications unloaded.
    // Only the badge number. This is what a page load needs — the navbar renders
    // a count, and the list is not worth fetching until the drawer is opened.
    async getNotificationsCount({ commit, state }) {
      if (state.role == 'superadmin') return;
      const { data } = await instance.get('/notifications/count', {
        params: subjectIdsParam(state)
      });
      commit('updateState', { notificationsUnread: data.count });
    },

    // One page of the list. `cursor` continues from a previous page; omitting it
    // loads the first and replaces whatever was there.
    async getNotifications({ commit, state }, { cursor } = {}) {
      if (state.role == 'superadmin') return;
      commit('updateState', { isNotificationsLoading: true });
      try {
        const { data } = await instance.get('/notifications', {
          params: { ...subjectIdsParam(state), ...(cursor && { cursor }) }
        });
        commit('updateState', {
          notifications: cursor ? [...state.notifications, ...data.notifications] : data.notifications,
          notificationsCursor: data.cursor ?? null,
          notificationsHasMore: !!data.hasMore,
          isNotificationsLoaded: true
        });
      } finally {
        commit('updateState', { isNotificationsLoading: false });
      }
    },
    async getSubjectSettings({ commit, state }) {
      const { data } = await instance.get(`/${state.role}/subject-settings`);
      commit('updateState', data);
    },
    async getCourse({ commit, state }, { courseId }) {
      const { data } = await instance.get(`/${state.role}/course/${courseId}`);
      commit('updateState', data);
    },
    async getCourses({ commit, state }, payload = {}) {
      const { filters } = payload;
      const data = await lms.fetchCourses(state.role, filters);
      commit('updateState', data);
    },
    async getLessons({ commit, state }, { courseId, filters }) {
      const data = await lms.fetchLessons(state.role, courseId, filters);
      commit('updateState', data);
    },
    async getConnections({ commit }, { courseId }) {
      const { data } = await instance.get(`/connections/${courseId}`);
      commit('updateState', data);
    },
    async getTasks({ commit, state }, { lessonId }) {
      const { data } = await instance.get(`/${state.role}/tasks/${lessonId}`);
      commit('setQuiz', data);
    },
    async getLessonsFavorite({ commit, state }, { courseId }) {
      const { data } = await instance.get(`/${state.role}/lessons-favorite/${courseId}`);
      commit('updateState', data);
    },
    async getCourseTip({ commit, state }, { tipId }) {
      const { data } = await instance.get(`/${state.role}/tip/${tipId}`);
      commit('updateState', data);
    },
    async getGroups({ commit, state }, { page, limit, filters }) {
      const { data } = await instance.post(`/${state.role}/groups`, { filters }, { params: { page, limit } });
      commit('updateState', data);
    },
    async getGroupsInfo({ commit, state }, { page, limit, filters }) {
      const { data } = await instance.post(`/${state.role}/groups-info`, { filters }, { params: { page, limit } });
      commit('updateState', data);
    },
    async getGroupsList({ commit, state }, { filters }) {
      const { data } = await instance.post(`/${state.role}/groups-list`, { filters });
      commit('updateState', data);
    },
    async getNotes({ commit }, { courseId }) {
      const { data } = await instance.get(`/student/notes/${courseId}`);
      commit('updateState', data);
    },
    async getStudents({ commit, state }, { page, limit, sortBy, sortOrder, filters }) {
      const { data } = await instance.post(
        `/${state.role}/students`,
        { filters },
        { params: { page, limit, sortBy, sortOrder } }
      );
      commit('updateState', data);
    },
    async getStudent({ commit, state }) {
      const { data } = await instance.get(`/${state.role}/student`);
      commit('updateState', data);
    },
    async getRating({ commit }, { courseId }) {
      const data = await lms.fetchRating(courseId);
      commit('updateState', data);
    },
    async getRewards({ commit }) {
      const { data } = await instance.get('/stats/rewards');
      commit('updateState', data);
    },
    async getStatsSubject({ commit }) {
      const { data } = await instance.get(`/stats/subject`);
      commit('updateState', data);
    },
    async getStatsCourseLessons({ commit, state }, { courseId, groupId, sortBy, sortOrder }) {
      const data = await lms.fetchCourseLessonsStats(state.role, { courseId, groupId }, { sortBy, sortOrder });
      commit('updateState', data);
    },
    async getStatsCourseStudents({ commit, state }, { courseId, page, limit, sortBy, sortOrder, filters }) {
      const data = await lms.fetchCourseStudentsStats(
        state.role,
        { courseId, filters },
        { page, limit, sortBy, sortOrder }
      );
      commit('updateState', data);
    },
    async getStatsCourseFunnels({ commit, state }, { courseId, groupId, granularity }) {
      const { data } = await instance.post(`/${state.role}/course-stats/funnel`, { courseId, groupId, granularity });
      commit('updateState', data);
    },
    async getStatsLesson({ commit, state }, { lessonId, taskTypes, filters }) {
      const data = await lms.fetchLessonStats(state.role, { lessonId, taskTypes, filters });
      commit('updateState', data);
    },
    async handleTaskAnswer({ commit }, taskData) {
      const { data } = await instance.post('/student/task/handle-answer', taskData);
      const { lesson, task, taskNext, user } = data;
      commit('updateLesson', lesson);
      commit('updateTask', task);
      if (taskNext) commit('pushTask', taskNext);
      if (user) commit('updateUserdata', { user });
    },
    async checkTaskTeacher(state, taskData) {
      await instance.post('/teacher/task/check', taskData);
      const notification = state.state.notifications.find(
        (e) => e.type == 'task_check_required' && e.lessonId == taskData.lessonId
      );
      if (notification) notification.count -= 1;
    },
    async checkLesson({ commit }, lessonData) {
      const {
        data: { user }
      } = await instance.post('/student/lesson/check', lessonData);
      if (user) commit('updateUserdata', { user });
    },
    async getCertificate({ commit }, { userId, courseId }) {
      const { data } = await instance.get(`/certificate/${userId}/${courseId}`);
      commit('updateState', data);
    }
  }
});
